import React, { Children, ComponentPropsWithRef, useCallback, useEffect, useState } from "react";
import { EmblaOptionsType, EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import "./Carousel.scss";

type PropType = {
  options?: EmblaOptionsType;
  children?: React.ReactNode;
};

const Carousel: React.FC<PropType> = (props) => {
  const { children, options } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);
  const count = Children.count(children);
  if (count === 0) return null;

  return (
    <section className='carousel'>
      <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
      <div className='carousel-viewport' ref={emblaRef}>
        <div className='carousel-container'>{children}</div>
      </div>
      <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
    </section>
  );
};
export default Carousel;

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

const usePrevNextButtons = (emblaApi: EmblaCarouselType | undefined): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

const PrevButton: React.FC<ComponentPropsWithRef<"button">> = (props) => {
  const { children, ...restProps } = props;

  return (
    <button className='carousel-button carousel-button--prev' type='button' {...restProps}>
      <div className='ui icon'>
        <i className='arrow left icon tw-m-0'></i>
      </div>
      {children}
    </button>
  );
};

const NextButton: React.FC<ComponentPropsWithRef<"button">> = (props) => {
  const { children, ...restProps } = props;

  return (
    <button className='carousel-button carousel-button--next' type='button' {...restProps}>
      <div className='ui icon'>
        <i className='arrow right icon tw-m-0'></i>
      </div>
      {children}
    </button>
  );
};
